import { SITE_URL } from "../../config/constants.js";

const EXTERNAL_LINK_REL_VALUES = ["noopener", "noreferrer"];
const BLANK_TARGET = "_blank";

function getSiteDomain(): string {
    return new URL(SITE_URL).hostname.replace(/^www\./, "");
}

function isHttpAbsoluteUrl(url: string): boolean {
    return /^(https?:)?\/\//i.test(url.trim());
}

function parseAbsoluteUrl(url: string): URL | null {
    const trimmedUrl = url.trim();
    if (!isHttpAbsoluteUrl(trimmedUrl)) return null;

    try {
        return new URL(trimmedUrl, SITE_URL);
    } catch {
        return null;
    }
}

function mergeRelValues(rel: string | null | undefined, values: string[]): string {
    const relValues = new Set((rel || "").split(/\s+/).filter(Boolean));

    for (const value of values) {
        relValues.add(value);
    }

    return Array.from(relValues).join(" ");
}

function splitPathSuffix(url: string): { path: string; suffix: string } {
    const queryIndex = url.indexOf("?");
    const hashIndex = url.indexOf("#");
    const suffixIndex = [queryIndex, hashIndex]
        .filter((index) => index !== -1)
        .sort((left, right) => left - right)[0];

    if (suffixIndex === undefined) {
        return { path: url, suffix: "" };
    }

    return {
        path: url.slice(0, suffixIndex),
        suffix: url.slice(suffixIndex),
    };
}

/**
 * Strips the site domain from a URL, converting absolute site URLs to relative paths.
 * Handles https/http and www/non-www variants.
 *
 * @param url - The URL to transform
 * @returns The relative path if it's a site URL, otherwise the original URL unchanged
 *
 * @example
 * stripSiteDomain('https://skywire.co.uk/about') // '/about'
 * stripSiteDomain('https://www.skywire.co.uk/services?foo=bar') // '/services?foo=bar'
 * stripSiteDomain('https://example.com/page') // 'https://example.com/page'
 * stripSiteDomain('/contact') // '/contact'
 */
export function stripSiteDomain(url: string | null | undefined): string {
    if (!url) return "";

    const trimmedUrl = url.trim();
    if (!trimmedUrl) return "";

    const siteDomain = getSiteDomain();

    // Build a regex pattern to match the site domain with optional www and http/https
    // Pattern matches: https://skywire.co.uk, https://www.skywire.co.uk, http:// and // variants
    // The (?=\/|$) ensures we match at a path boundary (/ or end of string)
    const pattern = new RegExp(
        `^(https?:)?\\/\\/(www\\.)?${siteDomain.replace(/\./g, "\\.")}(?=\\/|$)`,
        "i",
    );

    // Replace the domain with empty string, keeping the path
    const result = trimmedUrl.replace(pattern, "");

    // If the URL was transformed and now starts without a slash, add one
    // This handles the edge case of the homepage 'https://skywire.co.uk' -> ''
    if (result === "" && pattern.test(trimmedUrl)) {
        return "/";
    }

    return result || trimmedUrl;
}

/**
 * Normalizes CMS path slugs to the canonical internal URL shape.
 *
 * @example
 * normalizePathSlug('news/post/') // '/news/post'
 * normalizePathSlug('https://skywire.co.uk/news/post/') // '/news/post'
 */
export function normalizePathSlug(value: string | null | undefined): string {
    if (!value) return "";

    const trimmedValue = value.trim();
    if (!trimmedValue) return "";

    const internalUrl = stripSiteDomain(trimmedValue).trim();
    if (!internalUrl || isHttpAbsoluteUrl(internalUrl)) return internalUrl;

    const { path: rawPath, suffix } = splitPathSuffix(internalUrl);
    const pathWithoutLeadingSlashes = rawPath.replace(/^\/+/, "");
    let path = pathWithoutLeadingSlashes ? `/${pathWithoutLeadingSlashes}` : "/";

    if (path !== "/") {
        path = path.replace(/\/+$/, "") || "/";
    }

    return `${path}${suffix}`;
}

export function getPathSlugLookupVariants(value: string | null | undefined): string[] {
    const normalizedSlug = normalizePathSlug(value);
    if (!normalizedSlug || isHttpAbsoluteUrl(normalizedSlug))
        return normalizedSlug ? [normalizedSlug] : [];

    const { path, suffix } = splitPathSuffix(normalizedSlug);
    if (path === "/") return [normalizedSlug];

    return [`${path}${suffix}`, `${path}/${suffix}`];
}

/**
 * Strips site domain URLs from href attributes within an HTML string.
 * Used for rich text content that may contain embedded links.
 *
 * @param html - The HTML string to process
 * @returns The HTML string with site URLs converted to relative paths
 *
 * @example
 * stripSiteDomainFromHtml('<a href="https://skywire.co.uk/about">About</a>')
 * // '<a href="/about">About</a>'
 */
export function stripSiteDomainFromHtml(html: string | null | undefined): string {
    if (!html) return "";

    const siteDomain = getSiteDomain();

    // Pattern to match href attributes containing site URLs
    // Captures: href="https://skywire.co.uk/path" or href='//www.skywire.co.uk/path'
    // The (?=\/|["']) ensures we match at a path boundary (/ or end of the href value)
    const hrefPattern = new RegExp(
        `((?:^|\\s)href\\s*=\\s*["'])(https?:)?\\/\\/(www\\.)?${siteDomain.replace(/\./g, "\\.")}(\\/[^"']*|(?=["']))`,
        "gi",
    );

    // Replace matched hrefs, keeping the path portion
    return html.replace(hrefPattern, (_match, prefix, _protocol, _www, path) => {
        // Ensure path starts with /
        const relativePath = path || "/";
        return `${prefix}${relativePath}`;
    });
}

export function isExternalHttpUrl(url: string | null | undefined): boolean {
    if (!url) return false;

    const parsedUrl = parseAbsoluteUrl(url);
    if (!parsedUrl) return false;

    const siteDomain = getSiteDomain();
    const linkDomain = parsedUrl.hostname.replace(/^www\./, "");

    return linkDomain !== siteDomain;
}

export function getLinkAttributes(
    href: string | null | undefined,
    options: { target?: string | null; rel?: string | null } = {},
): { target?: string; rel?: string } {
    const isExternal = isExternalHttpUrl(href);
    const target = isExternal ? BLANK_TARGET : options.target || undefined;
    const rel =
        isExternal || target === BLANK_TARGET
            ? mergeRelValues(options.rel, EXTERNAL_LINK_REL_VALUES)
            : options.rel || undefined;

    return {
        ...(target ? { target } : {}),
        ...(rel ? { rel } : {}),
    };
}

export function addExternalLinkAttributesToHtml(html: string | null | undefined): string {
    if (!html) return "";

    return html.replace(/<a\b([^>]*)>/gi, (match, attributes) => {
        const hrefMatch = attributes.match(/(^|\s)href\s*=\s*(["'])(.*?)\2/i);
        if (!hrefMatch) return match;

        const targetMatch = attributes.match(/(^|\s)target\s*=\s*(["'])(.*?)\2/i);
        const relMatch = attributes.match(/(^|\s)rel\s*=\s*(["'])(.*?)\2/i);
        const linkAttributes = getLinkAttributes(hrefMatch[3], {
            target: targetMatch?.[3],
            rel: relMatch?.[3],
        });

        let nextAttributes = attributes;

        if (linkAttributes.target) {
            if (targetMatch) {
                nextAttributes = nextAttributes.replace(
                    targetMatch[0],
                    `${targetMatch[1]}target=${targetMatch[2]}${linkAttributes.target}${targetMatch[2]}`,
                );
            } else {
                nextAttributes += ` target="${linkAttributes.target}"`;
            }
        }

        if (linkAttributes.rel) {
            if (relMatch) {
                nextAttributes = nextAttributes.replace(
                    relMatch[0],
                    `${relMatch[1]}rel=${relMatch[2]}${linkAttributes.rel}${relMatch[2]}`,
                );
            } else {
                nextAttributes += ` rel="${linkAttributes.rel}"`;
            }
        }

        return `<a${nextAttributes}>`;
    });
}

export function prepareHtmlLinks(html: string | null | undefined): string {
    return addExternalLinkAttributesToHtml(stripSiteDomainFromHtml(html));
}
